import React, { useEffect, useState, useMemo } from 'react';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartOptions,
  ChartData
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
// HTML Legend Plugin based on Chart.js documentation
const getOrCreateLegendList = (chart: any, id: string) => {
  const legendContainer = document.getElementById(id);
  let listContainer = legendContainer?.querySelector('ul');
  if (!listContainer) {
    listContainer = document.createElement('ul');
    listContainer.style.display = 'flex';
    listContainer.style.flexDirection = 'row'; // Horizontal layout like other charts
    listContainer.style.flexWrap = 'nowrap'; // Prevent wrapping to second row
    listContainer.style.overflow = 'hidden'; // Hide overflow items
    listContainer.style.margin = '0';
    listContainer.style.padding = '0';
    listContainer.style.marginBottom = '20px'; // Bottom margin for spacing
    legendContainer?.appendChild(listContainer);
  }
  return listContainer;
};

const htmlLegendPlugin = {
  id: 'htmlLegend',
  afterUpdate(chart: any, args: any, options: any) {
    const ul = getOrCreateLegendList(chart, options.containerID);
    // Remove old legend items
    while (ul.firstChild) {
      ul.firstChild.remove();
    }
    // Reuse the built-in legendItems generator
    const items = chart.options.plugins.legend.labels.generateLabels(chart);

    // Calculate how many items can fit based on actual legend item width
    const containerWidth = ul.parentElement?.offsetWidth || 800;
    const estimatedItemWidth = 180; // More accurate width per legend item
    const maxItemsPerRow = Math.floor(containerWidth / estimatedItemWidth);
    const itemsPerRow = Math.max(3, Math.min(maxItemsPerRow, items.length)); // At least 3 items
    const itemsToShow = options.showAllLegends ? items.length : Math.min(itemsPerRow, items.length);
    const hasMoreItems = items.length > itemsPerRow;

    items.slice(0, itemsToShow).forEach((item: any) => {
      const li = document.createElement('li');
      li.style.alignItems = 'center';
      li.style.cursor = 'pointer';
      li.style.display = 'flex';
      li.style.flexDirection = 'row';
      li.style.marginRight = '15px'; // Horizontal spacing between legend items
      li.onclick = () => {
        const { type } = chart.config;
        if (type === 'pie' || type === 'doughnut') {
          chart.toggleDataVisibility(item.index);
        } else {
          chart.setDatasetVisibility(item.datasetIndex, !chart.isDatasetVisible(item.datasetIndex));
        }
        chart.update();
      };
      // Color box - CIRCULAR for pie chart
      const boxSpan = document.createElement('span');
      boxSpan.style.background = item.hidden ? '#ddd' : item.fillStyle; // Use fillStyle for pie chart
      boxSpan.style.borderRadius = '50%'; // Make it circular
      boxSpan.style.display = 'inline-block';
      boxSpan.style.flexShrink = '0';
      boxSpan.style.height = '15px';
      boxSpan.style.marginRight = '8px';
      boxSpan.style.width = '15px';
      boxSpan.style.opacity = item.hidden ? '0.5' : '1';
      // Text
      const textContainer = document.createElement('p');
      textContainer.style.color = item.hidden ? '#aaa' : item.fontColor;
      textContainer.style.margin = '0';
      textContainer.style.padding = '0';
      textContainer.style.textDecoration = item.hidden ? 'line-through' : 'none';
      textContainer.style.fontFamily = 'Inter';
      textContainer.style.fontWeight = '400';
      textContainer.style.fontStyle = 'Regular';
      textContainer.style.fontSize = '13px';
      textContainer.style.lineHeight = '18px';
      textContainer.style.letterSpacing = '0.32px';
      const text = document.createTextNode(item.text);
      textContainer.appendChild(text);
      li.appendChild(boxSpan);
      li.appendChild(textContainer);
      ul.appendChild(li);
    });

    // Add "See more" button if there are more items
    if (hasMoreItems && !options.showAllLegends) {
      const seeMoreLi = document.createElement('li');
      seeMoreLi.style.alignItems = 'center';
      seeMoreLi.style.cursor = 'pointer';
      seeMoreLi.style.display = 'flex';
      seeMoreLi.style.flexDirection = 'row';
      seeMoreLi.style.marginRight = '15px';
      seeMoreLi.style.padding = '4px 8px';
      seeMoreLi.style.borderRadius = '9999px'; // Rounded full like AppliedFiltersDisplay
      seeMoreLi.style.backgroundColor = 'transparent';
      seeMoreLi.style.fontSize = '14px';
      seeMoreLi.style.color = '#6b7280'; // text-tertiary-text color
      seeMoreLi.style.fontFamily = 'Inter';
      seeMoreLi.style.transition = 'background-color 0.2s';
      seeMoreLi.style.gap = '4px';

      // Add hover effect - match AppliedFiltersDisplay exactly
      seeMoreLi.onmouseenter = () => {
        seeMoreLi.style.backgroundColor = '#e6e6e6'; // hover:bg-[#e6e6e6]
      };
      seeMoreLi.onmouseleave = () => {
        seeMoreLi.style.backgroundColor = 'transparent';
      };

      seeMoreLi.onclick = () => {
        if (options.onToggleShowMore) {
          options.onToggleShowMore();
        }
      };

      // Create text and chevron icon
      const hiddenCount = items.length - itemsPerRow;
      const seeMoreText = document.createTextNode(`See more (${hiddenCount})`);
      seeMoreLi.appendChild(seeMoreText);

      // Add chevron down icon (simple SVG)
      const chevronIcon = document.createElement('span');
      chevronIcon.innerHTML = '▼';
      chevronIcon.style.fontSize = '10px';
      chevronIcon.style.marginLeft = '4px';
      seeMoreLi.appendChild(chevronIcon);

      ul.appendChild(seeMoreLi);
    } else if (hasMoreItems && options.showAllLegends) {
      // Add "See less" button when all legends are shown
      const seeLessLi = document.createElement('li');
      seeLessLi.style.alignItems = 'center';
      seeLessLi.style.cursor = 'pointer';
      seeLessLi.style.display = 'flex';
      seeLessLi.style.flexDirection = 'row';
      seeLessLi.style.marginRight = '15px';
      seeLessLi.style.padding = '4px 8px';
      seeLessLi.style.borderRadius = '9999px'; // Rounded full like AppliedFiltersDisplay
      seeLessLi.style.backgroundColor = 'transparent';
      seeLessLi.style.fontSize = '14px';
      seeLessLi.style.color = '#6b7280'; // text-tertiary-text color
      seeLessLi.style.fontFamily = 'Inter';
      seeLessLi.style.transition = 'background-color 0.2s';
      seeLessLi.style.gap = '4px';

      // Add hover effect - match AppliedFiltersDisplay exactly
      seeLessLi.onmouseenter = () => {
        seeLessLi.style.backgroundColor = '#e6e6e6'; // hover:bg-[#e6e6e6]
      };
      seeLessLi.onmouseleave = () => {
        seeLessLi.style.backgroundColor = 'transparent';
      };

      seeLessLi.onclick = () => {
        if (options.onToggleShowMore) {
          options.onToggleShowMore();
        }
      };

      // Create text and chevron icon
      const seeLessText = document.createTextNode('See less');
      seeLessLi.appendChild(seeLessText);

      // Add chevron up icon (simple SVG)
      const chevronIcon = document.createElement('span');
      chevronIcon.innerHTML = '▲';
      chevronIcon.style.fontSize = '10px';
      chevronIcon.style.marginLeft = '4px';
      seeLessLi.appendChild(chevronIcon);

      ul.appendChild(seeLessLi);
    }
  }
};

const ExternalLabelsPlugin = {
  id: 'externalLabels',
  afterDatasetsDraw(chart: any) {
    const { ctx } = chart;
    const dataset = chart.data.datasets[0];
    const meta = chart.getDatasetMeta(0);

    if (!dataset || !meta) return;

    const total = dataset.data.reduce((sum: number, value: number) => sum + value, 0);

    // First pass: Calculate all label positions
    interface LabelInfo {
      index: number;
      x: number;
      y: number;
      centerX: number;
      centerY: number;
      midAngle: number;
      outerRadius: number;
      text: string;
      percentage: number;
      isLeft: boolean;
    }

    const labels: LabelInfo[] = [];

    meta.data.forEach((element: any, index: number) => {
      const { x, y } = element.getProps(['x', 'y'], true);
      const startAngle = element.startAngle;
      const endAngle = element.endAngle;
      const midAngle = (startAngle + endAngle) / 2;

      const value = dataset.data[index];
      const percentage = (value / total) * 100;

      // Adjust label distance based on percentage to reduce overlap issues
      // Smaller percentages need more distance, medium need less
      const labelDistance = percentage < 3 ? 35 : (percentage < 10 ? 25 : 30);
      const radius = element.outerRadius + labelDistance;
      const labelX = x + Math.cos(midAngle) * radius;
      const labelY = y + Math.sin(midAngle) * radius;

      const label = chart.data.labels[index];

      // Skip only 0.0% labels (show everything > 0%)
      if (percentage < 0.05) return;

      const text = `${label}: ${percentage.toFixed(1)}%`;
      const isLeft = labelX <= x;

      labels.push({
        index,
        x: labelX,
        y: labelY,
        centerX: x,
        centerY: y,
        midAngle,
        outerRadius: element.outerRadius,
        text,
        percentage,
        isLeft
      });
    });

    // Second pass: Resolve overlapping labels
    // Sort labels by Y position within each side (left/right)
    const leftLabels = labels.filter(l => l.isLeft).sort((a, b) => a.y - b.y);
    const rightLabels = labels.filter(l => !l.isLeft).sort((a, b) => a.y - b.y);

    const minVerticalSpacing = 18; // Minimum pixels between label centers

    // Resolve overlaps for each side
    const resolveOverlaps = (sideLabels: LabelInfo[]) => {
      for (let i = 1; i < sideLabels.length; i++) {
        const prevLabel = sideLabels[i - 1];
        const currentLabel = sideLabels[i];

        const spacing = currentLabel.y - prevLabel.y;
        if (spacing < minVerticalSpacing) {
          // Push current label down to avoid overlap
          currentLabel.y = prevLabel.y + minVerticalSpacing;
        }
      }
    };

    resolveOverlaps(leftLabels);
    resolveOverlaps(rightLabels);

    // Third pass: Draw all labels with adjusted positions
    const allLabels = [...leftLabels, ...rightLabels];

    allLabels.forEach((labelInfo) => {
      const { x, y, centerX, centerY, midAngle, outerRadius, text, isLeft } = labelInfo;

      ctx.fillStyle = '#374151';
      ctx.font = 'bold 11px Arial';
      ctx.textAlign = isLeft ? 'right' : 'left';
      ctx.textBaseline = 'middle';

      ctx.fillText(text, x, y);

      // Draw straight connector line from pie slice to label
      ctx.strokeStyle = '#9CA3AF';
      ctx.lineWidth = 1;
      ctx.beginPath();

      // Start point: edge of pie slice
      const lineStartX = centerX + Math.cos(midAngle) * (outerRadius + 5);
      const lineStartY = centerY + Math.sin(midAngle) * (outerRadius + 5);

      // End point: near the label
      const lineEndX = x + (isLeft ? 5 : -5);

      // Draw straight line from slice to label
      ctx.moveTo(lineStartX, lineStartY);
      ctx.lineTo(lineEndX, y);
      ctx.stroke();
    });
  }
};

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels, htmlLegendPlugin, ExternalLabelsPlugin);

interface ChartSeries {
  name: string;
  data: number[];
  stack: string;
}

interface UniversalChartData {
  type: 'pie' | 'bar' | 'column' | 'stackedColumn' | 'line';
  title?: string;
  subtitle?: string;
  categories: string[];
  series: ChartSeries[];
  showLegend?: boolean;
  colors?: string[];
  showDataLabels?: boolean;
  labelsMode?: 'names' | 'values' | 'percentages' | 'names-percentages' | 'none';
  stacking?: 'normal';
  xAxisTitle?: string;
  yAxisTitle?: string;
}

interface PieChartProps {
  data: UniversalChartData;
  showDataLabels?: boolean;
  labelsMode?: 'names' | 'values' | 'percentages' | 'names-percentages' | 'none';
  containerHeight?: number;
}

interface ProcessedDataPoint {
  name: string;
  value: number;
  displayValue: number;
  percentage?: number;
  category?: string;
  stack?: string;
  legend?: string;
}

const sortProcessedData = (processedData: ProcessedDataPoint[]): ProcessedDataPoint[] => {
  return [...processedData].sort((a, b) => b.value - a.value);
};

const PieChart: React.FC<PieChartProps> = ({
  data,
  showDataLabels = false,
  labelsMode = 'names-percentages',
  containerHeight: propContainerHeight
}) => {
  const [chartData, setChartData] = useState<ProcessedDataPoint[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showAllLegends, setShowAllLegends] = useState(false);
  const [itemsPerRow, setItemsPerRow] = useState(3);
  const [selectedSeries, setSelectedSeries] = useState<Set<number>>(new Set());

  const colors = [
    "#53eafd",
    "#ffdf20",
    "#bbf451",
    "#ffa2a2",
    "#c4b4ff",
    "#8ec5ff",
    "#ffb86a",
    "#7bf1a8",
    "#fda5d5",
    "#46ecd5",
    "#ffd230",
    "#a3b3ff",
    "#ffa1ad",
    "#5ee9b5",
    "#dab2ff",
    "#74d4ff",
    "#f4a8ff"
  ];

  const formatValue = (value: number): string => {
    if (value >= 1000000000) {
      return (value / 1000000000).toFixed(1) + 'B';
    } else if (value >= 1000000) {
      return (value / 1000000).toFixed(1) + 'M';
    } else if (value >= 1000) {
      return (value / 1000).toFixed(1) + 'K';
    }
    return Math.round(value).toString();
  };

  const calculateItemsPerRow = (data: ProcessedDataPoint[]) => {
    const container = document.querySelector('#legend-container-pie') as HTMLElement;
    if (!container || data.length === 0) {
      setItemsPerRow(3);
      return;
    }

    const containerWidth = container.offsetWidth;
    const total = data.reduce((sum, d) => sum + d.value, 0);

    // For small containers, show fewer items
    if (containerWidth < 400) {
      setItemsPerRow(Math.min(2, data.length));
      return;
    }

    // Measure all legend item widths first
    const itemWidths: number[] = [];
    for (let i = 0; i < data.length; i++) {
      const item = data[i];
      const testItem = document.createElement('div');
      testItem.style.cssText = `
        position: absolute;
        visibility: hidden;
        white-space: nowrap;
        font-family: Inter;
        font-size: 13px;
        font-weight: 400;
      `;
      testItem.textContent = `${item.name}: ${formatValue(item.value)} (${((item.value / total) * 100).toFixed(1)}%)`;
      document.body.appendChild(testItem);
      const itemWidth = testItem.offsetWidth + 30; // 30px for gap and circular icon
      document.body.removeChild(testItem);
      itemWidths.push(itemWidth);
    }

    // Try fitting items from the end to find how many fit with "See more" button
    for (let itemsToShow = data.length; itemsToShow >= 1; itemsToShow--) {
      const itemsWidth = itemWidths.slice(0, itemsToShow).reduce((sum, w) => sum + w, 0);
      const remainingItems = data.length - itemsToShow;

      // If all items fit, no button needed
      if (remainingItems === 0) {
        if (itemsWidth <= containerWidth) {
          setItemsPerRow(itemsToShow);
          return;
        }
        continue;
      }

      // Calculate button width for this scenario: "See more (X)"
      const buttonText = `See more (${remainingItems})`;
      const buttonWidth = buttonText.length * 8 + 40; // Approximate: 8px per char + padding

      if (itemsWidth + buttonWidth <= containerWidth) {
        setItemsPerRow(itemsToShow);
        return;
      }
    }

    setItemsPerRow(1);
  };

  // Toggle series selection
  const toggleSeriesSelection = (seriesIndex: number) => {
    const allSelected = selectedSeries.size === 0 || selectedSeries.size === chartData.length;

    if (allSelected) {
      // When all are selected (or default state), clicking one selects ONLY that item
      setSelectedSeries(new Set([seriesIndex]));
    } else {
      // When some items are selected, toggle behavior
      const newSelectedSeries = new Set(selectedSeries);
      if (newSelectedSeries.has(seriesIndex)) {
        newSelectedSeries.delete(seriesIndex);
        // If nothing left selected, reset to show all
        if (newSelectedSeries.size === 0) {
          setSelectedSeries(new Set());
        } else {
          setSelectedSeries(newSelectedSeries);
        }
      } else {
        newSelectedSeries.add(seriesIndex);
        setSelectedSeries(newSelectedSeries);
      }
    }
  };

  // Select all series
  const selectAllSeries = () => {
    const allSeries = new Set(chartData.map((_, index) => index));
    setSelectedSeries(allSeries);
  };

  // Memoize the processed and sorted data
  const processedData = useMemo(() => {
    try {
      if (!data || !data.series || !Array.isArray(data.series) || data.series.length === 0) {
        return [];
      }

      if (!Array.isArray(data.categories)) {
        return [];
      }

      let rawProcessedData: ProcessedDataPoint[] = [];

      // Check if this is data converted from bar/column chart (has stacking or multiple series with all zero values in main stack)
      const isConvertedFromBarChart = data.stacking === 'normal' ||
        (data.series.length > 1 && data.series.some(s => s.stack === 'secondary'));

      if (isConvertedFromBarChart || data.type === 'line') {
        // Handle data converted from bar/column charts
        // Combine all series data by summing values across categories
        const categoryTotals: { [key: string]: number } = {};

        data.series.forEach((series) => {
          series.data.forEach((value, index) => {
            const category = data.categories[index];
            if (category && value > 0) {
              categoryTotals[category] = (categoryTotals[category] || 0) + value;
            }
          });
        });

        rawProcessedData = Object.entries(categoryTotals)
          .filter(([_, value]) => value > 0)
          .map(([category, value]) => ({
            name: category,
            value: value,
            displayValue: value,
            category: category
          }));

      } else {
        // Handle regular pie chart data (original format)
        // For pie charts, we typically use the first series
        const primarySeries = data.series[0];

        if (primarySeries && primarySeries.data) {
          rawProcessedData = data.categories.map((category, index) => {
            const value = primarySeries.data[index] || 0;

            return {
              name: category,
              value: value,
              displayValue: value,
              category: category
            };
          }).filter(item => item.value > 0); // Filter out zero values
        }
      }

      // Sort the data by value (largest first)
      return sortProcessedData(rawProcessedData);
    } catch (err) {
      console.error('Error processing pie chart data:', err);
      return [];
    }
  }, [data]);

  useEffect(() => {
    setError(null);

    if (processedData.length === 0) {
      if (data && data.series && data.series.length > 0) {
        setError('No valid data points found');
      }
      setChartData([]);
    } else {
      setChartData(processedData);
    }
  }, [processedData, data]);

  // Calculate items per row when chartData changes
  useEffect(() => {
    if (chartData.length > 0) {
      setTimeout(() => {
        calculateItemsPerRow(chartData);
      }, 100);
    }
  }, [chartData]);

  // Recalculate on window resize
  useEffect(() => {
    const handleResize = () => {
      if (chartData.length > 0) {
        calculateItemsPerRow(chartData);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [chartData]);


  if (error) {
    return <div className="text-center p-4 text-red-500">{error}</div>;
  }

  if (!data || !data.series || data.series.length === 0) {
    return <div className="text-center p-4 text-gray-500 pt-36 pb-36">No data available for chart</div>;
  }

  if (!chartData.length) {
    return <div className="text-center p-4 text-gray-500 pt-36 pb-36">No data available for chart</div>;
  }

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  const createDataLabelsConfig = () => {
    if (!showDataLabels || labelsMode === 'none') {
      return { display: false };
    }

    // For external labels, we'll use the custom plugin instead of datalabels
    if (labelsMode === 'names-percentages' || labelsMode === 'percentages') {
      return { display: false }; // Disable default datalabels when using external
    }

    const baseConfig = {
      color: '#323f4a',
      font: {
        size: 10
      },
      padding: 2,
      textAlign: 'center' as const,
      backgroundColor: 'transparent',
      display: function (context: any) {
        const value = context.dataset.data[context.dataIndex];
        return value > 0;
      }
    };

    switch (labelsMode) {
      case 'names':
        return {
          ...baseConfig,
          formatter: (value: number, context: any) => {
            if (value <= 0) return '';
            return context.chart.data.labels[context.dataIndex];
          }
        };

      case 'values':
        return {
          ...baseConfig,
          formatter: (value: number, context: any) => {
            if (value <= 0) return '';
            return formatValue(value);
          }
        };

      default:
        return { display: false };
    }
  };

  // Filter chart data based on selected series (show only selected items)
  // If nothing is selected, show all (default state)
  const filteredChartData = selectedSeries.size > 0
    ? chartData.filter((_, index) => selectedSeries.has(index))
    : chartData; // Show all if none selected

  const chartJSData: ChartData<'pie'> = {
    labels: filteredChartData.map(item => item.name),
    datasets: [{
      data: filteredChartData.map(item => item.value),
      backgroundColor: filteredChartData.map((_, index) => {
        const originalIndex = chartData.findIndex(item => item.name === filteredChartData[index].name);
        return colors[originalIndex % colors.length] + '80';
      }),
      borderColor: filteredChartData.map((_, index) => {
        const originalIndex = chartData.findIndex(item => item.name === filteredChartData[index].name);
        return colors[originalIndex % colors.length];
      }),
      borderWidth: 1
    }]
  };

  const options: ChartOptions<'pie'> = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        bottom: 45,
        left: 0,
        right: 0,
        top: 45
      }
    },
    plugins: {
      legend: {
        display: false, // Hide built-in legend, but keep it enabled for HTML legend plugin
        labels: {
          generateLabels: function (chart: any) {
            const chartData = chart.data;
            if (chartData.labels && chartData.datasets.length) {
              const chartTotal = chartData.datasets[0].data.reduce((sum: number, value: any) => {
                const numValue = typeof value === 'number' ? value : 0;
                return sum + numValue;
              }, 0);

              return chartData.labels.map((label: any, i: number) => {
                const value = chartData.datasets[0].data[i] as number;
                const percentage = ((value / chartTotal) * 100).toFixed(1);
                const formattedValue = formatValue(value);
                const backgroundColor = Array.isArray(chartData.datasets[0].backgroundColor)
                  ? chartData.datasets[0].backgroundColor[i]
                  : chartData.datasets[0].backgroundColor;
                const borderColor = Array.isArray(chartData.datasets[0].borderColor)
                  ? chartData.datasets[0].borderColor[i]
                  : chartData.datasets[0].borderColor;

                return {
                  text: `${label}: ${formattedValue} (${percentage}%)`,
                  fillStyle: backgroundColor as string,
                  strokeStyle: borderColor as string,
                  fontColor: '#323f4a',
                  lineWidth: 1,
                  hidden: false,
                  index: i
                };
              });
            }
            return [];
          }
        }
      },
      title: {
        display: false, // Disable built-in title, use HTML title instead
      },
      tooltip: {
        displayColors: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        usePointStyle: true, // This forces circles instead of squares
        callbacks: {
          title: function (context: any[]) {
            return context[0]?.label || '';
          },
          label: function (context: any) {
            const value = context.parsed as number;
            const chartTotal = context.chart.data.datasets[0].data.reduce((sum: number, val: any) => {
              const numValue = typeof val === 'number' ? val : 0;
              return sum + numValue;
            }, 0);
            const percentage = ((value / chartTotal) * 100).toFixed(1);
            const formattedValue = formatValue(value);
            return `${formattedValue} (${percentage}%)`;
          },
          labelColor: function (context: any) {
            // Return the correct color for the tooltip circle (filled)
            const dataIndex = context.dataIndex;
            const backgroundColor = Array.isArray(context.chart.data.datasets[0].backgroundColor)
              ? context.chart.data.datasets[0].backgroundColor[dataIndex]
              : context.chart.data.datasets[0].backgroundColor;
            const borderColor = Array.isArray(context.chart.data.datasets[0].borderColor)
              ? context.chart.data.datasets[0].borderColor[dataIndex]
              : context.chart.data.datasets[0].borderColor;

            // Remove transparency from backgroundColor for solid fill
            const solidColor = backgroundColor.toString().replace(/80$/, '');

            return {
              borderColor: borderColor as string,
              backgroundColor: solidColor // Fill the entire circle with legend color
            };
          },
          afterLabel: function (context: any) {
            return context.label;
          }
        }
      },
      datalabels: createDataLabelsConfig()
    }
  };

  return (
    <>
      <div className="w-full">
        {/* Chart Title */}
        {data.title && (
          <div className="text-h4 text-primary-text mt-2 mb-4 text-left">{data.title}</div>
        )}
        {/* React Legend Container - Top */}
        {data.showLegend !== false && (
          <div id="legend-container-pie" className="mb-4">
            <ul key={showAllLegends ? 'expanded' : 'collapsed'} className="flex" style={{
              flexWrap: showAllLegends ? 'wrap' : 'nowrap',
              gap: '8px'
            }}>
              {(() => {
                const itemsToShow = showAllLegends ? chartData.length : itemsPerRow;
                const hasMoreItems = chartData.length > itemsPerRow;
                const hasSelectedItems = selectedSeries.size > 0;
                const allItemsSelected = selectedSeries.size === chartData.length;
                const shouldShowSelectAll = hasSelectedItems && !allItemsSelected;
                // If nothing is selected, treat all as selected (default state)
                const effectiveSelectedSeries = selectedSeries.size > 0 ? selectedSeries : new Set(chartData.map((_, index) => index));

                return (
                  <>
                    {/* "Select All" option - only show when some (but not all) items are selected */}
                    {shouldShowSelectAll && (
                      <li
                        className="flex items-center gap-1 cursor-pointer"
                        onClick={selectAllSeries}
                      >
                        <span
                          className="w-4 h-4 rounded-full flex-shrink-0 border-2"
                          style={{
                            backgroundColor: 'transparent',
                            borderColor: '#4F46E5',
                            borderRadius: '50%'
                          }}
                        />
                        <span
                          className="text-sm font-medium whitespace-nowrap"
                          style={{
                            fontFamily: 'Inter',
                            fontWeight: '500',
                            fontSize: '13px',
                            lineHeight: '18px',
                            letterSpacing: '0.32px',
                            color: '#4F46E5'
                          }}
                        >
                          Select All
                        </span>
                      </li>
                    )}

                    {chartData.slice(0, itemsToShow).map((item, index) => (
                      <li
                        key={index}
                        className="flex items-center gap-1 cursor-pointer"
                        onClick={() => toggleSeriesSelection(index)}
                      >
                        <span
                          className="w-4 h-4 rounded-full flex-shrink-0"
                          style={{
                            backgroundColor: colors[index % colors.length],
                            borderRadius: '50%',
                            opacity: effectiveSelectedSeries.has(index) ? 1 : 0.5
                          }}
                        />
                        <span
                          className="text-sm whitespace-nowrap"
                          style={{
                            fontFamily: 'Inter',
                            fontWeight: '400',
                            fontSize: '13px',
                            lineHeight: '18px',
                            letterSpacing: '0.32px',
                            color: effectiveSelectedSeries.has(index) ? '#323f4a' : '#aaa',
                            textDecoration: 'none'
                          }}
                        >
                          {item.name}: {formatValue(item.value)} ({((item.value / total) * 100).toFixed(1)}%)
                        </span>
                      </li>
                    ))}
                    {hasMoreItems && !showAllLegends && (
                      <li
                        className="flex items-center gap-1 cursor-pointer px-2 rounded-full text-sm transition-colors"
                        style={{
                          color: '#6b7280',
                          fontFamily: 'Inter',
                          backgroundColor: '#f3f4f6',
                          border: 'none'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e6e6e6'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        onClick={() => setShowAllLegends(true)}
                      >
                        <span>See more ({chartData.length - itemsPerRow})</span>
                        <span style={{ fontSize: '10px' }}>▼</span>
                      </li>
                    )}
                    {hasMoreItems && showAllLegends && (
                      <li
                        className="flex items-center gap-1 cursor-pointer px-2 rounded-full text-sm transition-colors"
                        style={{
                          color: '#6b7280',
                          fontFamily: 'Inter',
                          backgroundColor: 'transparent'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e6e6e6'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        onClick={() => setShowAllLegends(false)}
                      >
                        <span>See less</span>
                        <span style={{ fontSize: '10px' }}>▲</span>
                      </li>
                    )}
                  </>
                );
              })()}
            </ul>
          </div>
        )}
        {/* Chart Container - Full width */}
        <div style={{ width: '100%', height: `${propContainerHeight || 400}px` }}>
          <Pie
            data={chartJSData}
            options={options}
          />
        </div>
      </div>
    </>
  );
};

export default PieChart;